import React, { useState, useMemo, useCallback } from "react";
import { Row, Col, Card, Typography, Button, Tag, message } from "antd";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  CoffeeOutlined,
  WalletOutlined,
  FileTextOutlined,
  BarChartOutlined,
  AccountBookOutlined,
  RightOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  REPORT_REGISTRY,
  REPORT_CATEGORIES,
  ReportCategory,
  ReportDefinition,
} from "../../services/reportRegistry";
import { reservationPrintService } from "../../services/adminService";
import ReportViewer from "./ReportViewer";
import DatePrintModal from "../reservations/DatePrintModal";

const { Title, Text } = Typography;

const CATEGORY_META: Record<
  ReportCategory,
  { icon: React.ReactNode; color: string; description: string }
> = {
  revenue: {
    icon: <DollarOutlined />,
    color: "#b79a68",
    description: "Track revenue across all departments",
  },
  services: {
    icon: <CoffeeOutlined />,
    color: "#722ed1",
    description: "Laundry, rentals, and ancillary services",
  },
  payments: {
    icon: <WalletOutlined />,
    color: "#52c41a",
    description: "Payment processing and guest folios",
  },
  tax: {
    icon: <FileTextOutlined />,
    color: "#faad14",
    description: "Tax, discounts, and compliance reports",
  },
  analytics: {
    icon: <BarChartOutlined />,
    color: "#1890ff",
    description: "Menu item and category performance",
  },
  finance: {
    icon: <AccountBookOutlined />,
    color: "#eb2f96",
    description: "General ledger, P&L, cash book and daily close",
  },
};

const REPORT_DESCRIPTIONS: Record<string, string> = {
  "daily-sales": "Revenue breakdown across all sources by day",
  "restaurant-sales": "Total restaurant revenue by date",
  "take-away-sales": "Take away order revenue summary",
  "dine-in-sales": "Dine-in revenue with table utilization",
  "room-service-sales": "Room service orders and revenue",
  "room-revenue": "Room charge revenue from folios",
  "meal-breakdown": "Breakfast, lunch, and dinner revenue split",
  "monthly-revenue": "Revenue and booking trends over the last 6 months",
  "bookings-by-room-type": "Booking distribution and revenue by room category",
  laundry: "Laundry service orders and revenue",
  ironing: "Ironing service orders and revenue",
  "other-services": "All ancillary service charges",
  "car-rental": "Car rental service entries",
  "motorbike-rental": "Motorbike rental service entries",
  "car-wash": "Car wash service entries",
  cashier: "Transactions grouped by staff member",
  payments: "Payment collection by method and status",
  "outstanding-balances": "Guests with unpaid folio balances",
  "guest-folio": "Detailed folio for a specific booking",
  "weekly-statement": "Weekly revenue and payment summary",
  "monthly-statement": "Monthly revenue breakdown by category",
  tax: "VAT and tax collected from all sources",
  discounts: "All discounts applied across POS and folios",
  "void-cancelled": "Cancelled orders and bookings",
  "item-sales": "Top selling menu items by revenue",
  "category-sales": "Sales performance by menu category",
  "top-guests": "Top 10 guests ranked by total spend",
  "finance-profit-loss": "Income less cost of sales and operating expenses",
  "finance-trial-balance": "Opening, movement and closing balances for every account",
  "finance-day-book": "Every journal posted on a business date",
  "finance-cash-book": "Money in and out of each cash and bank account, with per-account running balances",
  "finance-daily-cash": "Opening cash, receipts, payments, bank deposits and closing cash by account and day",
  "finance-daily-card": "Card collections, settlements, processor fees and card-clearing balance by day",
  "finance-daily-bank": "Opening bank balance, cash deposits, card settlements, payments and closing balance by day",
  "finance-reconciliation": "Source systems compared against the general ledger",
  "finance-room-performance": "Occupancy, ADR and RevPAR derived from the ledger and the folio",
  "finance-restaurant-performance": "Covers, average ticket and channel revenue against the ledger",
  "finance-stock-valuation": "What is on the shelf, and whether GL 1200 agrees with it",
  "finance-food-cost": "Cost of goods issued against restaurant revenue, with recipe coverage",
  "finance-purchase-register": "Every stock invoice received, with the control that proves it posted",
  "finance-payroll-register":
    "Every employee line, what was withheld from it, and the control that ties it to the ledger",
};

const ReportsPage: React.FC = () => {
  const [activeReport, setActiveReport] = useState<ReportDefinition | null>(null);
  const [activeCategory, setActiveCategory] = useState<ReportCategory | "all">("all");
  const [printModalType, setPrintModalType] = useState<"occupancy" | "availability" | null>(null);
  const [printLoading, setPrintLoading] = useState(false);

  const filteredReports = useMemo(() => {
    if (activeCategory === "all") return REPORT_REGISTRY;
    return REPORT_REGISTRY.filter((r) => r.category === activeCategory);
  }, [activeCategory]);

  const handleDatePrint = useCallback(async (params: { date?: string }) => {
    if (!printModalType) return;
    setPrintLoading(true);
    try {
      const date = params.date || new Date().toISOString().split("T")[0];
      let blob: Blob;
      let filename: string;
      if (printModalType === "occupancy") {
        blob = await reservationPrintService.getOccupancyListPdf(date);
        filename = `occupancy-list-${date}.pdf`;
      } else {
        blob = await reservationPrintService.getAvailabilityPdf(date);
        filename = `room-availability-${date}.pdf`;
      }
      reservationPrintService.downloadPdf(blob, filename);
      message.success(`${printModalType === "occupancy" ? "Occupancy" : "Availability"} report downloaded`);
    } catch {
      message.error("Failed to generate PDF");
    } finally {
      setPrintLoading(false);
      setPrintModalType(null);
    }
  }, [printModalType]);

  if (activeReport) {
    return (
      <div style={{ padding: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => setActiveReport(null)}
          style={{ marginBottom: 16, paddingLeft: 0, color: "#666" }}
        >
          Back to Reports
        </Button>
        <ReportViewer report={activeReport} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, fontWeight: 600 }}>
          Reports & Analytics
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          View and export operational reports across all departments
        </Text>
      </div>

      {/* Category Pills */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <Button
          type={activeCategory === "all" ? "primary" : "default"}
          onClick={() => setActiveCategory("all")}
          style={
            activeCategory === "all"
              ? { background: "#b79a68", borderColor: "#b79a68" }
              : {}
          }
        >
          All Reports
        </Button>
        {Object.entries(REPORT_CATEGORIES).map(([key, cat]) => {
          const meta = CATEGORY_META[key as ReportCategory];
          return (
            <Button
              key={key}
              type={activeCategory === key ? "primary" : "default"}
              icon={meta.icon}
              onClick={() => setActiveCategory(key as ReportCategory)}
              style={
                activeCategory === key
                  ? { background: meta.color, borderColor: meta.color }
                  : {}
              }
            >
              {cat.label}
            </Button>
          );
        })}
      </div>

      {/* Quick Downloads */}
      <div style={{ marginBottom: 24 }}>
        <Text type="secondary" style={{ fontSize: 13, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Quick Downloads
        </Text>
        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <Card
            size="small"
            hoverable
            onClick={() => setPrintModalType("occupancy")}
            style={{ flex: 1, maxWidth: 280, borderRadius: 8, cursor: "pointer" }}
            styles={{ body: { padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 } }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "rgba(82, 196, 26, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#52c41a",
                flexShrink: 0,
              }}
            >
              <DownloadOutlined />
            </div>
            <div>
              <Text strong style={{ fontSize: 13, display: "block" }}>Occupancy List</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>PDF by date</Text>
            </div>
          </Card>
          <Card
            size="small"
            hoverable
            onClick={() => setPrintModalType("availability")}
            style={{ flex: 1, maxWidth: 280, borderRadius: 8, cursor: "pointer" }}
            styles={{ body: { padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 } }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "rgba(24, 144, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1890ff",
                flexShrink: 0,
              }}
            >
              <DownloadOutlined />
            </div>
            <div>
              <Text strong style={{ fontSize: 13, display: "block" }}>Room Availability</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>PDF by date</Text>
            </div>
          </Card>
        </div>
      </div>

      {/* Report Cards Grid */}
      <Row gutter={[16, 16]}>
        {filteredReports.map((report) => {
          const catMeta = CATEGORY_META[report.category];
          return (
            <Col xs={24} sm={12} lg={8} key={report.id}>
              <Card
                hoverable
                onClick={() => setActiveReport(report)}
                style={{
                  height: "100%",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                  transition: "all 0.2s",
                }}
                styles={{
                  body: { padding: "20px 24px", height: "100%", display: "flex", flexDirection: "column" },
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: `${catMeta.color}12`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      color: catMeta.color,
                      flexShrink: 0,
                    }}
                  >
                    {catMeta.icon}
                  </div>
                  <RightOutlined style={{ color: "#bbb", fontSize: 12, marginTop: 8 }} />
                </div>

                <Text strong style={{ fontSize: 15, display: "block", marginBottom: 6 }}>
                  {report.title}
                </Text>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5, flex: 1 }}>
                  {REPORT_DESCRIPTIONS[report.id] || "View report data"}
                </Text>

                <div style={{ marginTop: 12, display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {report.filters.includes("dateRange") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Date Range</Tag>
                  )}
                  {report.filters.includes("staff") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Staff</Tag>
                  )}
                  {report.filters.includes("orderType") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Order Type</Tag>
                  )}
                  {report.filters.includes("paymentMethod") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Payment</Tag>
                  )}
                  {report.filters.includes("month") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Monthly</Tag>
                  )}
                  {report.filters.includes("weekStart") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Weekly</Tag>
                  )}
                  {report.filters.includes("bookingId") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Booking</Tag>
                  )}
                  {report.filters.includes("room") && (
                    <Tag style={{ fontSize: 11, margin: 0 }}>Room</Tag>
                  )}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <DatePrintModal
        open={!!printModalType}
        title={printModalType === "occupancy" ? "Print Occupancy List" : "Print Room Availability"}
        type="single"
        onPrint={handleDatePrint}
        onCancel={() => setPrintModalType(null)}
        loading={printLoading}
      />
    </div>
  );
};

export default ReportsPage;
